import { ApiError, apiRequest } from '@lumlab/platform-product/web';
import { archivePartUrls, archiveTransfer, maxArchiveBytes, type ArchiveImport } from '../contract/archives.ts';

export const archiveApiPath = '/api/admin/products/tenders/archive-imports';

/** A small re-selection guard, not a full-file integrity claim. The worker validates every ZIP entry and CRC. */
export async function archiveFileToken(file: File) {
    if (file.size > maxArchiveBytes || file.size < 22) throw new ApiError('payload_too_large', 413);
    const sampleBytes = 256 * 1024;
    const [first, last] = await Promise.all([
        file.slice(0, sampleBytes).arrayBuffer(),
        file.slice(Math.max(sampleBytes, file.size - sampleBytes)).arrayBuffer(),
    ]);
    const metadata = new TextEncoder().encode(JSON.stringify([file.name, file.size, file.lastModified]));
    const input = new Uint8Array(metadata.length + first.byteLength + last.byteLength);
    input.set(metadata);
    input.set(new Uint8Array(first), metadata.length);
    input.set(new Uint8Array(last), metadata.length + first.byteLength);
    const digest = await crypto.subtle.digest('SHA-256', input);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Upload at most three slices concurrently. Reload/resume consults S3, never a browser's stale part list. */
export async function uploadArchive(
    file: File,
    item: ArchiveImport,
    options: { signal: AbortSignal; onProgress: (completedBytes: number) => void },
) {
    if (file.size !== item.sizeBytes || (await archiveFileToken(file)) !== item.fileToken)
        throw new Error('archive_wrong_file');
    const controller = new AbortController();
    const signal = AbortSignal.any([options.signal, controller.signal]);
    const transfer = archiveTransfer.parse(await apiRequest(`${archiveApiPath}/${item.id}/transfer`, { signal }));
    const completed = new Set(transfer.completedParts);
    const missing = Array.from({ length: Math.ceil(file.size / transfer.partBytes) }, (_, index) => index + 1).filter(
        (part) => !completed.has(part),
    );
    let cursor = 0,
        completedBytes = transfer.completedBytes;
    options.onProgress(completedBytes);
    async function worker() {
        while (cursor < missing.length) {
            signal.throwIfAborted();
            const number = missing[cursor++]!;
            const start = (number - 1) * transfer.partBytes,
                end = Math.min(file.size, start + transfer.partBytes);
            for (let attempt = 0; ; attempt++) {
                try {
                    const urls = archivePartUrls.parse(
                        await apiRequest(`${archiveApiPath}/${item.id}/parts`, {
                            method: 'POST',
                            body: { numbers: [number] },
                            signal,
                        }),
                    );
                    const url = urls.parts.find((part) => part.number === number)?.url;
                    if (!url) throw new ApiError('internal', 500);
                    const response = await fetch(url, {
                        method: 'PUT',
                        body: file.slice(start, end),
                        credentials: 'omit',
                        referrerPolicy: 'no-referrer',
                        signal,
                    });
                    if (!response.ok) throw new ApiError('network', response.status);
                    break;
                } catch (error) {
                    if (signal.aborted || attempt >= 2) throw error;
                    await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
                }
            }
            completedBytes += end - start;
            options.onProgress(completedBytes);
        }
    }
    try {
        await Promise.all(Array.from({ length: Math.min(3, missing.length) }, worker));
        signal.throwIfAborted();
        await apiRequest(`${archiveApiPath}/${item.id}/complete`, { method: 'POST', signal });
    } finally {
        controller.abort();
    }
}
